import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Camera, Upload, X, Plus, Trash2, Truck, Users } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import BackNavigation from "../components/BackNavigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DPREntry = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [boqItems, setBoqItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [projectContractors, setProjectContractors] = useState([]); // Contractors assigned to this project
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    boq_item_id: "",
    activity: "",
    planned_quantity: "",
    actual_quantity: "",
    unit: "Cum",
    workforce_count: "",
    equipment_used: "",
    weather: "Clear",
    remarks: "",
    delays: "",
    contractor_id: "",
    contractor_name: ""
  });
  // Multiple deployed persons support
  const [deployedPersons, setDeployedPersons] = useState([]);
  const [materialsConsumed, setMaterialsConsumed] = useState([]);
  const [equipmentUsed, setEquipmentUsed] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchBOQItems();
    fetchMaterials();
    fetchEquipment();
    fetchContractors();
    fetchEmployees();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error("Failed to fetch project");
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await apiClient.get(`/equipment/${projectId}`);
      setEquipmentList(response.data);
    } catch (error) {
      console.error("Failed to fetch equipment");
    }
  };

  const fetchContractors = async () => {
    try {
      // Fetch all contractors
      const allContractorsRes = await apiClient.get(`/contractors`);
      setContractors(allContractorsRes.data);
      
      // Fetch project-assigned contractors for highlighting
      const projectContractorsRes = await apiClient.get(`/contractors/project/${projectId}`);
      setProjectContractors(projectContractorsRes.data.map(c => c.id));
    } catch (error) {
      console.error("Failed to fetch contractors");
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await apiClient.get(`/employees/project/${projectId}`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees");
    }
  };

  const fetchBOQItems = async () => {
    try {
      const response = await apiClient.get(`/boq/${projectId}`);
      setBoqItems(response.data);
    } catch (error) {
      console.error("Failed to fetch BOQ items");
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await apiClient.get(`/materials/${projectId}`);
      setMaterials(response.data);
    } catch (error) {
      console.error("Failed to fetch materials");
    }
  };

  const handleBOQSelect = (boqItemId) => {
    const selectedItem = boqItems.find(item => item.id === boqItemId);
    if (selectedItem) {
      setFormData(prev => ({
        ...prev,
        boq_item_id: boqItemId,
        activity: selectedItem.description,
        unit: selectedItem.unit,
        planned_quantity: (selectedItem.planned_quantity - selectedItem.completed_quantity).toString()
      }));
    }
  };

  const addMaterialRow = () => {
    setMaterialsConsumed([...materialsConsumed, { material_id: "", quantity: "" }]);
  };

  const removeMaterialRow = (index) => {
    setMaterialsConsumed(materialsConsumed.filter((_, i) => i !== index));
  };

  const updateMaterialRow = (index, field, value) => {
    const updated = [...materialsConsumed];
    updated[index][field] = value;
    setMaterialsConsumed(updated);
  };

  const addEquipmentRow = () => {
    setEquipmentUsed([...equipmentUsed, { equipment_id: "", hours_used: "", operator_name: "" }]);
  };

  const removeEquipmentRow = (index) => {
    setEquipmentUsed(equipmentUsed.filter((_, i) => i !== index));
  };

  const updateEquipmentRow = (index, field, value) => {
    const updated = [...equipmentUsed];
    updated[index][field] = value;
    setEquipmentUsed(updated);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const response = await apiClient.post('/upload-photo', formData);
        return response.data.photo_url;
      });
      const uploadedUrls = await Promise.all(uploadPromises);
      setPhotos([...photos, ...uploadedUrls]);
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (error) {
      toast.error("Failed to upload photos");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate materials
    const validMaterials = materialsConsumed
      .filter(m => m.material_id && m.quantity)
      .map(m => ({
        material_id: m.material_id,
        quantity: parseFloat(m.quantity)
      }));

    // Validate equipment
    const validEquipment = equipmentUsed
      .filter(eq => eq.equipment_id && eq.hours_used)
      .map(eq => ({
        equipment_id: eq.equipment_id,
        hours_used: parseFloat(eq.hours_used),
        operator_name: eq.operator_name
      }));

    // Build equipment_used string for display
    const equipmentString = validEquipment.map(eq => {
      const eqInfo = equipmentList.find(e => e.id === eq.equipment_id);
      return eqInfo ? `${eqInfo.name} (${eq.hours_used} hrs)` : '';
    }).filter(Boolean).join(', ');

    // Build deployed persons data
    const deployedPersonIds = deployedPersons.map(p => p.id);
    const deployedPersonNames = deployedPersons.map(p => p.name).join(', ');

    try {
      const dprData = {
        ...formData,
        project_id: projectId,
        planned_quantity: parseFloat(formData.planned_quantity),
        actual_quantity: parseFloat(formData.actual_quantity),
        workforce_count: parseInt(formData.workforce_count),
        equipment_used: equipmentString || formData.equipment_used,
        materials_consumed: validMaterials,
        photos: photos,
        // Multiple deployed persons - store first one in main fields for backward compatibility
        deployed_person_id: deployedPersonIds[0] || "",
        deployed_person_name: deployedPersonNames,
        // Store all deployed persons in new field
        deployed_persons: deployedPersons
      };
      
      const dprResponse = await apiClient.post('/dpr', dprData);
      const dprId = dprResponse.data.id;
      
      // Create equipment daily logs linked to this DPR
      for (const eq of validEquipment) {
        try {
          await apiClient.post('/equipment/daily-log', {
            project_id: projectId,
            equipment_id: eq.equipment_id,
            date: formData.date,
            hours_used: eq.hours_used,
            operator_name: eq.operator_name,
            activity: formData.activity,
            location: "",
            dpr_id: dprId,
            remarks: `Linked to DPR: ${formData.activity}`
          });
        } catch (err) {
          console.error("Failed to create equipment log", err);
        }
      }
      
      // Check if selected BOQ item has a recipe and auto-create material outwards
      if (formData.boq_item_id && parseFloat(formData.actual_quantity) > 0) {
        const selectedBoq = boqItems.find(b => b.id === formData.boq_item_id);
        if (selectedBoq && selectedBoq.material_recipe && selectedBoq.material_recipe.length > 0) {
          try {
            const params = new URLSearchParams({
              project_id: projectId,
              boq_item_id: formData.boq_item_id,
              boq_quantity: formData.actual_quantity,
              outward_date: formData.date,
              issued_to: formData.activity,
              purpose: "DPR Work Execution",
              dpr_id: dprId,
              remarks: `Auto-generated from DPR: ${formData.activity}`
            });
            
            const outwardResponse = await apiClient.post(`/materials/outward/boq-linked?${params.toString()}`);
            
            if (outwardResponse.data.stock_warnings && outwardResponse.data.stock_warnings.length > 0) {
              toast.warning(
                `DPR saved. ${outwardResponse.data.outwards_created.length} material outwards created. ${outwardResponse.data.stock_warnings.length} had insufficient stock.`,
                { duration: 5000 }
              );
            } else if (outwardResponse.data.outwards_created.length > 0) {
              toast.success(`DPR saved with ${outwardResponse.data.outwards_created.length} auto-generated material outwards`);
            }
          } catch (outwardError) {
            console.error("Failed to create BOQ-linked outward from DPR:", outwardError);
            // Don't fail the DPR creation, just warn
            toast.warning("DPR saved but failed to auto-create material outwards. You can create them manually.");
          }
        } else {
          toast.success("DPR Entry Created Successfully");
        }
      } else {
        toast.success("DPR Entry Created Successfully");
      }
      
      navigate(`/dashboard/${projectId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create DPR entry");
    }
  };

  if (!project) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="max-w-4xl">
      <BackNavigation title="DPR Entry" projectName={project?.name} />
      
      <div className="mb-8">
        <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">DAILY PROGRESS REPORT</h1>
        <p className="text-slate-600 mt-2 font-body">{project.name}</p>
      </div>

      <form onSubmit={handleSubmit} className="stat-card">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date</label>
            <input
              type="date"
              data-testid="dpr-date-input"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
            />
          </div>

          {/* Contractor Selection */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contractor</label>
            <select
              data-testid="dpr-contractor-select"
              value={formData.contractor_id}
              onChange={(e) => {
                const selectedContractor = contractors.find(c => c.id === e.target.value);
                setFormData({ 
                  ...formData, 
                  contractor_id: e.target.value,
                  contractor_name: selectedContractor ? selectedContractor.name : ""
                });
              }}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Select Contractor --</option>
              {/* Project-assigned contractors first (highlighted) */}
              {contractors.filter(c => projectContractors.includes(c.id)).length > 0 && (
                <optgroup label="📌 Project Contractors">
                  {contractors.filter(c => projectContractors.includes(c.id)).map((contractor) => (
                    <option key={contractor.id} value={contractor.id}>
                      ⭐ {contractor.name} {contractor.contact_person ? `(${contractor.contact_person})` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              {/* All other contractors */}
              {contractors.filter(c => !projectContractors.includes(c.id)).length > 0 && (
                <optgroup label="All Contractors">
                  {contractors.filter(c => !projectContractors.includes(c.id)).map((contractor) => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.name} {contractor.contact_person ? `(${contractor.contact_person})` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {/* Multiple Deployed Persons Selection */}
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
              Deployed Persons / Supervisors 
              <span className="text-slate-500 font-normal ml-2">(Select multiple)</span>
            </label>
            
            {/* Selected Persons Tags */}
            {deployedPersons.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {deployedPersons.map((person) => (
                  <span 
                    key={person.id} 
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                  >
                    <Users className="w-3 h-3" />
                    {person.name}
                    <button
                      type="button"
                      onClick={() => setDeployedPersons(deployedPersons.filter(p => p.id !== person.id))}
                      className="ml-1 hover:text-error"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {/* Dropdown to add more */}
            <select
              data-testid="dpr-deployed-person-select"
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  const selectedEmployee = employees.find(emp => emp.id === e.target.value);
                  if (selectedEmployee && !deployedPersons.find(p => p.id === selectedEmployee.id)) {
                    setDeployedPersons([...deployedPersons, {
                      id: selectedEmployee.id,
                      name: selectedEmployee.name,
                      designation: selectedEmployee.designation || selectedEmployee.category
                    }]);
                  }
                }
              }}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Add Deployed Person --</option>
              {employees
                .filter(emp => !deployedPersons.find(p => p.id === emp.id))
                .map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} - {emp.designation || emp.category || 'Staff'} {emp.employee_code ? `(${emp.employee_code})` : ''}
                  </option>
                ))
              }
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {employees.length} employees available • {deployedPersons.length} selected
            </p>
          </div>

          {/* Weather */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Weather</label>
            <select
              value={formData.weather}
              onChange={(e) => setFormData({ ...formData, weather: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="Clear">Clear</option>
              <option value="Cloudy">Cloudy</option>
              <option value="Rainy">Rainy</option>
              <option value="Stormy">Stormy</option>
              <option value="Hot">Hot</option>
              <option value="Cold">Cold</option>
            </select>
          </div>

          {/* BOQ Item Selection */}
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Link to BOQ Item (Optional)</label>
            <select
              data-testid="dpr-boq-select"
              value={formData.boq_item_id}
              onChange={(e) => handleBOQSelect(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Select BOQ Item to auto-fill --</option>
              {boqItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.material_recipe && item.material_recipe.length > 0 ? '🧪 ' : ''}
                  {item.item_code} - {item.description} (Remaining: {(item.planned_quantity - item.completed_quantity).toFixed(2)} {item.unit})
                </option>
              ))}
            </select>
            {formData.boq_item_id && (() => {
              const selectedBoq = boqItems.find(b => b.id === formData.boq_item_id);
              if (selectedBoq && selectedBoq.material_recipe && selectedBoq.material_recipe.length > 0) {
                return (
                  <div className="mt-2 p-3 bg-success/10 border border-success/20 rounded-sm">
                    <p className="text-sm text-success font-medium mb-1">🧪 BOQ Recipe Active</p>
                    <p className="text-xs text-slate-600">
                      {selectedBoq.material_recipe.length} materials will be auto-deducted based on actual quantity entered.
                    </p>
                    <ul className="text-xs text-slate-500 mt-1 space-y-0.5">
                      {selectedBoq.material_recipe.map((r, idx) => (
                        <li key={idx}>• {r.material_name}: {r.quantity_per_unit} {r.unit} per {selectedBoq.unit}</li>
                      ))}
                    </ul>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Activity / Work Description</label>
            <input
              type="text"
              data-testid="dpr-activity-input"
              required
              value={formData.activity}
              onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Earthwork Excavation, Concrete Laying, Asphalt Paving"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Planned Quantity</label>
            <input
              type="number"
              step="0.01"
              data-testid="dpr-planned-quantity-input"
              required
              value={formData.planned_quantity}
              onChange={(e) => setFormData({ ...formData, planned_quantity: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              placeholder="100.00"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Actual Quantity Achieved</label>
            <input
              type="number"
              step="0.01"
              data-testid="dpr-actual-quantity-input"
              required
              value={formData.actual_quantity}
              onChange={(e) => setFormData({ ...formData, actual_quantity: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              placeholder="95.50"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Unit</label>
            <select
              data-testid="dpr-unit-select"
              required
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="Cum">Cum (Cubic Meter)</option>
              <option value="Sqm">Sqm (Square Meter)</option>
              <option value="Rm">Rm (Running Meter)</option>
              <option value="MT">MT (Metric Ton)</option>
              <option value="Nos">Nos (Numbers)</option>
              <option value="Km">Km (Kilometer)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Workforce Count</label>
            <input
              type="number"
              data-testid="dpr-workforce-input"
              required
              value={formData.workforce_count}
              onChange={(e) => setFormData({ ...formData, workforce_count: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
              placeholder="25"
            />
          </div>

          {/* Equipment Used Section */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-slate-700 uppercase tracking-widest">
                <Truck className="w-4 h-4 inline mr-2" />
                Equipment Used (Logs hours to Equipment Management)
              </label>
              <button
                type="button"
                onClick={addEquipmentRow}
                className="text-xs flex items-center space-x-1 text-accent hover:text-primary"
              >
                <Plus className="w-4 h-4" />
                <span>Add Equipment</span>
              </button>
            </div>
            
            {equipmentUsed.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-300 rounded-sm">
                No equipment added. Click "Add Equipment" to log equipment usage.
              </p>
            ) : (
              <div className="space-y-2">
                {equipmentUsed.map((eq, index) => (
                  <div key={eq.equipment_id || `eq-row-${index}`} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-sm">
                    <select
                      value={eq.equipment_id}
                      onChange={(e) => updateEquipmentRow(index, 'equipment_id', e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                      data-testid={`equipment-select-${index}`}
                    >
                      <option value="">Select Equipment</option>
                      {equipmentList.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name} ({e.equipment_id}) - {e.status}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.1"
                      value={eq.hours_used}
                      onChange={(e) => updateEquipmentRow(index, 'hours_used', e.target.value)}
                      placeholder="Hours"
                      className="w-24 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                      data-testid={`equipment-hours-${index}`}
                    />
                    <input
                      type="text"
                      value={eq.operator_name}
                      onChange={(e) => updateEquipmentRow(index, 'operator_name', e.target.value)}
                      placeholder="Operator"
                      className="w-32 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                      data-testid={`equipment-operator-${index}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeEquipmentRow(index)}
                      className="p-2 text-error hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Fallback Equipment Text Input */}
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Additional Equipment Notes</label>
            <input
              type="text"
              data-testid="dpr-equipment-input"
              value={formData.equipment_used}
              onChange={(e) => setFormData({ ...formData, equipment_used: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Hired Excavator (2 hrs), Manual compactor"
            />
          </div>

          {/* Materials Consumed Section */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-slate-700 uppercase tracking-widest">Materials Consumed (Auto-deducts from Inventory)</label>
              <button
                type="button"
                onClick={addMaterialRow}
                className="text-xs flex items-center space-x-1 text-primary hover:text-accent"
              >
                <Plus className="w-4 h-4" />
                <span>Add Material</span>
              </button>
            </div>
            
            {materialsConsumed.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-300 rounded-sm">
                No materials added. Click "Add Material" to record material consumption.
              </p>
            ) : (
              <div className="space-y-2">
                {materialsConsumed.map((mat, index) => (
                  <div key={mat.material_id || `mat-row-${index}`} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-sm">
                    <select
                      value={mat.material_id}
                      onChange={(e) => updateMaterialRow(index, 'material_id', e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                      data-testid={`material-select-${index}`}
                    >
                      <option value="">Select Material</option>
                      {materials.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} (Stock: {m.current_stock?.toFixed(2) || 0} {m.unit})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      value={mat.quantity}
                      onChange={(e) => updateMaterialRow(index, 'quantity', e.target.value)}
                      placeholder="Qty"
                      className="w-32 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                      data-testid={`material-qty-${index}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeMaterialRow(index)}
                      className="p-2 text-error hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Weather Conditions</label>
            <select
              data-testid="dpr-weather-select"
              required
              value={formData.weather}
              onChange={(e) => setFormData({ ...formData, weather: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="Clear">Clear</option>
              <option value="Partly Cloudy">Partly Cloudy</option>
              <option value="Cloudy">Cloudy</option>
              <option value="Rain">Rain</option>
              <option value="Heavy Rain">Heavy Rain</option>
              <option value="Fog">Fog</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Delays (if any)</label>
            <input
              type="text"
              data-testid="dpr-delays-input"
              value={formData.delays}
              onChange={(e) => setFormData({ ...formData, delays: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Rain delay 2 hours, Equipment breakdown"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks / Additional Notes</label>
            <textarea
              data-testid="dpr-remarks-input"
              required
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              rows="3"
              placeholder="Any observations, issues, or important notes..."
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
              <Camera className="w-4 h-4 inline mr-2" />
              Site Photos
            </label>
            <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
                data-testid="dpr-photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className="cursor-pointer inline-flex items-center space-x-2 btn-outline"
              >
                <Upload className="w-4 h-4" />
                <span>{uploading ? "Uploading..." : "Upload Photos"}</span>
              </label>
              <p className="text-xs text-slate-500 mt-2">Upload site progress photos (JPG, PNG)</p>
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-4">
                {photos.map((photo, index) => (
                  <div key={photo || `photo-${index}`} className="relative group">
                    <img
                      src={`${BACKEND_URL}${photo}`}
                      alt={`Site photo ${index + 1}`}
                      className="w-full h-32 object-cover rounded-sm border border-slate-300"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-slate-200">
          <button
            type="button"
            onClick={() => navigate(`/dashboard/${projectId}`)}
            className="btn-outline"
          >
            Cancel
          </button>
          <button type="submit" data-testid="submit-dpr-button" className="btn-primary flex items-center space-x-2">
            <span>Submit DPR</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default DPREntry;
